import React, { useMemo, useEffect, useRef } from 'react'
import {
  Controller,
  FieldValues,
  Path,
  useWatch,
  useFieldArray
} from 'react-hook-form'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Icon,
  Switch,
  Textfield
} from '@clientbase/clientbase-library'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'

import { theme } from 'styles/theme'

import {
  IdentifyInternalProps,
  InternalIdentifier
} from './IdentifyInternal.interface'

const IdentifyInternal = <T extends FieldValues>({
  control,
  setValue,
  tooltipText = 'Os Identificadores Internos serão mostrados nos detalhes da sua cobranças e apenas você consegue ver esses campos.'
}: IdentifyInternalProps<T>) => {
  const hasIdentifyInternal = useWatch({
    control,
    name: 'hasIdentifyInternal' as Path<T>
  })

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'internalIdentifiers' as any
  })

  // Watch dos valores atuais para sincronização
  const watchedIdentifiers = (useWatch({
    control,
    name: 'internalIdentifiers' as any
  }) || []) as InternalIdentifier[]

  // Ref para controlar se já sincronizou os dados de edição
  const hasSyncedEditData = useRef(false)

  // Sincronizar dados de edição com useFieldArray uma única vez
  useEffect(() => {
    if (hasSyncedEditData.current) return

    // Na edição o useFieldArray costuma já nascer com os itens. Marca como
    // sincronizado aqui: senão, ao remover o último, fields fica vazio com o
    // useWatch ainda defasado e o replace abaixo ressuscita o item removido.
    if (fields.length > 0) {
      hasSyncedEditData.current = true
      return
    }

    if (Array.isArray(watchedIdentifiers) && watchedIdentifiers.length > 0) {
      // Se há dados no formulário mas não no useFieldArray, sincronizar
      replace(watchedIdentifiers as any)
      hasSyncedEditData.current = true
    }
  }, [watchedIdentifiers, fields.length, replace])

  // Calcula se deve estar expandido baseado apenas no estado hasIdentifyInternal
  // Permite que o usuário feche o accordion mesmo com dados preenchidos
  // useMemo para evitar recálculos desnecessários que causam "piscar"
  const shouldBeExpanded = useMemo(() => {
    if (setValue) {
      return !!hasIdentifyInternal
    }
    // Se não há setValue (modo somente leitura), mostra baseado nos campos existentes
    return fields.length > 0
  }, [hasIdentifyInternal, fields.length, setValue])

  const handleAccordionChange = (
    _: React.ChangeEvent<object>,
    isExpanded: boolean
  ) => {
    if (!setValue) return

    // Evita mudanças desnecessárias se já está no estado correto
    if (isExpanded === shouldBeExpanded) return

    setValue('hasIdentifyInternal' as Path<T>, isExpanded as any)

    // Se está expandindo e não há campos, adiciona o primeiro
    if (isExpanded && fields.length === 0) {
      append({ name: '', value: '' } as any)
    }

    // Se está contraindo, remove identificadores vazios
    if (!isExpanded) {
      const emptyIndices: number[] = []
      fields.forEach((_, idx) => {
        const identifier = watchedIdentifiers[idx]
        if (!identifier?.name?.trim() && !identifier?.value?.trim()) {
          emptyIndices.push(idx)
        }
      })
      // Remove de trás pra frente para não alterar os índices
      emptyIndices.reverse().forEach((idx) => remove(idx))
    }
  }

  return (
    <Accordion
      expanded={shouldBeExpanded}
      onChange={handleAccordionChange}
      sx={{
        border: '1px solid #E8EBE8',
        borderLeft: '3px solid',
        borderLeftColor: 'primary.main',
        borderRadius: '8px !important',
        backgroundColor: '#FAFBFA',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          margin: 0,
          backgroundColor: '#fff'
        }
      }}
    >
      <AccordionSummary
        hasSwitch
        expandIcon={<Switch checked={shouldBeExpanded} />}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="smBold">Identificador interno</Typography>
          <Tooltip
            enterTouchDelay={0}
            arrow
            placement="top"
            title={tooltipText}
          >
            <Box>
              <Icon
                icon="info"
                sx={{
                  fontSize: '1.2rem',
                  mt: '3px',
                  color: theme.palette.neutral[700]
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2 }}>
        <Stack spacing={1}>
          {fields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'grid',
                gap: '10px',
                gridTemplateColumns: '1fr 1fr 40px',
                alignItems: 'center'
              }}
            >
              {/* Campo Nome */}
              <Controller
                control={control}
                name={`internalIdentifiers.${index}.name` as Path<T>}
                rules={{ required: 'Campo obrigatório.' }}
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error }
                }) => (
                  <Textfield
                    inputRef={ref}
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    name={`internalIdentifiers.${index}.name`}
                    label={index === 0 ? 'Nome do campo' : ''}
                    placeholder="Descrição"
                    value={value || ''}
                    onChange={onChange}
                    hiddenLabel={index !== 0}
                    sx={{
                      '& .MuiFormHelperText-root': {
                        minHeight: '20px'
                      }
                    }}
                  />
                )}
              />

              {/* Campo Informação */}
              <Controller
                control={control}
                name={`internalIdentifiers.${index}.value` as Path<T>}
                rules={{ required: 'Campo obrigatório.' }}
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error }
                }) => (
                  <Textfield
                    inputRef={ref}
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    name={`internalIdentifiers.${index}.value`}
                    label={index === 0 ? 'Informação' : ''}
                    placeholder="Descrição"
                    value={value || ''}
                    onChange={onChange}
                    hiddenLabel={index !== 0}
                    sx={{
                      '& .MuiFormHelperText-root': {
                        minHeight: '20px'
                      }
                    }}
                  />
                )}
              />

              {/* Botão Delete */}
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  pt: index === 0 ? 3 : 0
                }}
              >
                <IconButton onClick={() => remove(index)}>
                  <Icon icon="close" colorSX="#777877" />
                </IconButton>
              </Box>
            </Box>
          ))}

          {/* Botão Adicionar */}
          {fields.length < 5 && (
            <>
              <Box
                sx={{
                  height: '1px',
                  width: '100%',
                  bgcolor: theme.palette.neutral[700]
                }}
              />

              <Button
                onClick={() => {
                  append({ name: '', value: '' } as any)
                }}
                variantButton="ghost"
                sx={{ width: 'fit-content', mt: '10px' }}
              >
                <Stack direction="row" gap="5px" alignItems="center">
                  <Icon
                    icon="add"
                    sx={{ color: theme.palette.neutral[400], fontSize: '18px' }}
                  />
                  <Typography
                    sx={{
                      color: theme.palette.neutral[400],
                      fontWeight: '600',
                      fontSize: '14px'
                    }}
                  >
                    Adicionar identificador
                  </Typography>
                </Stack>
              </Button>
            </>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

export default IdentifyInternal
